import { useState } from "react";
import { toast } from "sonner";
import { Camera, Megaphone, Sparkles, Users, Film, Award, CheckCircle2 } from "lucide-react";
import { submitBusinessInquiry } from "@/lib/api";

const SERVICES = [
  { icon: Megaphone, title: "Restaurant Marketing", desc: "Monthly retainers that drive covers, not just clicks." },
  { icon: Sparkles, title: "Branding & Identity", desc: "Visual identity systems built to outlast the trend cycle." },
  { icon: Camera, title: "Menu & Food Photography", desc: "Editorial-grade imagery for menus, social, and press kits." },
  { icon: Film, title: "Commercial Production", desc: "Cinematic spots for grand openings, anniversaries, and brand films." },
  { icon: Users, title: "Influencer & Social Media", desc: "Curated talent partnerships and full-service content management." },
  { icon: Award, title: "PR & Reputation", desc: "Press placement, awards strategy, and reputation management." },
];

const SERVICE_OPTIONS = SERVICES.map((s) => s.title).concat([
  "Grand Opening Campaign", "Recruitment Campaign", "Loyalty Program",
  "Online Ordering Strategy", "Franchise Marketing",
]);

const BUDGETS = ["< $5k", "$5k – $15k", "$15k – $50k", "$50k+"];

export default function Services() {
  const [form, setForm] = useState({
    business_name: "",
    contact_name: "",
    email: "",
    phone: "",
    services: [],
    message: "",
    budget: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const toggleService = (s) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s],
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.contact_name || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await submitBusinessInquiry(form);
      setSubmitted(true);
      toast.success("Pitch received. We'll be in touch within 48 hours.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="services-page" className="bg-sand-50">
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand-500 mb-4">
            Business Services
          </div>
          <h1 className="font-serif font-black text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-sand-900">
            We build the<br />brands behind<br />the country's best tables.
          </h1>
          <p className="mt-8 max-w-xl text-lg font-light text-sand-700 leading-relaxed">
            Tastebuds Studio is the marketing arm of the network — a full-service team
            of creatives, producers, and strategists working exclusively with restaurants,
            chefs, and hospitality brands across Houston, Atlanta, Dallas, Los Angeles,
            New Orleans, New York & San Diego.
          </p>
        </div>
        <div className="lg:col-span-5">
          <div className="img-zoom aspect-[4/5]">
            <img src="https://images.pexels.com/photos/17086289/pexels-photo-17086289.jpeg" alt="studio" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <section className="bg-white border-y border-sand-200 py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand-500 mb-3">What We Do</div>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-sand-900 leading-[1.05] mb-16 max-w-3xl">
            Six services. One <span className="italic text-brand-500">unfair</span> advantage.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
            {SERVICES.map(({ icon: Icon, title, desc }, i) => (
              <div key={title} className="border-t-2 border-sand-900 pt-6 animate-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                <Icon size={28} className="text-brand-500 mb-5" />
                <h3 className="font-serif text-2xl font-bold text-sand-900 mb-2">{title}</h3>
                <p className="text-sand-700 font-light leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pitch" className="max-w-5xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand-500 mb-3">Pitch Your Brand</div>
        <h2 className="font-serif text-4xl md:text-6xl font-bold text-sand-900 leading-[1.05] mb-12">
          Tell us about<br />your restaurant.
        </h2>

        {submitted ? (
          <div data-testid="inquiry-success" className="bg-white border-l-4 border-forest-500 p-10">
            <CheckCircle2 size={36} className="text-forest-500 mb-4" />
            <h3 className="font-serif text-3xl font-bold text-sand-900 mb-3">Pitch received.</h3>
            <p className="text-sand-700 font-light text-lg">
              Thanks, <span className="font-medium text-sand-900">{form.contact_name}</span>.
              Someone from the Tastebuds Studio team will respond within 48 hours.
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} data-testid="inquiry-form" className="bg-white border border-sand-200 p-8 md:p-12 space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Business Name *" testid="inquiry-business-name" value={form.business_name} onChange={(v) => setForm({ ...form, business_name: v })} />
              <Field label="Your Name *" testid="inquiry-contact-name" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} />
              <Field label="Email *" type="email" testid="inquiry-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <Field label="Phone" testid="inquiry-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
            </div>

            <div>
              <label className="block font-mono text-[10px] tracking-[0.25em] uppercase text-sand-700 mb-4">Services of Interest</label>
              <div className="flex flex-wrap gap-2">
                {SERVICE_OPTIONS.map((s) => (
                  <button
                    type="button"
                    key={s}
                    data-testid={`service-tag-${s.toLowerCase().replace(/[^a-z]/g, "-")}`}
                    onClick={() => toggleService(s)}
                    className={`px-4 py-2 font-mono text-[10px] tracking-[0.15em] uppercase border transition-all active:scale-95 ${
                      form.services.includes(s)
                        ? "bg-brand-500 text-sand-50 border-brand-500"
                        : "bg-transparent text-sand-900 border-sand-300 hover:border-sand-900"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block font-mono text-[10px] tracking-[0.25em] uppercase text-sand-700 mb-4">Budget Range</label>
              <div className="flex flex-wrap gap-2">
                {BUDGETS.map((b) => (
                  <button
                    type="button"
                    key={b}
                    data-testid={`budget-${b.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                    onClick={() => setForm({ ...form, budget: b })}
                    className={`px-5 py-2.5 font-mono text-[10px] tracking-[0.15em] uppercase border transition-all active:scale-95 ${
                      form.budget === b
                        ? "bg-sand-900 text-sand-50 border-sand-900"
                        : "bg-transparent text-sand-900 border-sand-300 hover:border-sand-900"
                    }`}
                  >
                    {b}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block font-mono text-[10px] tracking-[0.25em] uppercase text-sand-700 mb-3">
                Tell us about your project *
              </label>
              <textarea
                data-testid="inquiry-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={5}
                className="w-full bg-sand-50 border border-sand-300 px-4 py-3 font-sans text-sand-900 focus:outline-none focus:border-brand-500 transition-colors"
                placeholder="Tell us about your brand, your goals, your timeline…"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="inquiry-submit"
              className="w-full md:w-auto bg-sand-900 text-sand-50 px-10 py-4 font-mono text-[11px] font-bold tracking-[0.25em] uppercase hover:bg-brand-500 transition-colors active:scale-95 disabled:opacity-60"
            >
              {submitting ? "Sending…" : "Send Pitch →"}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="block font-mono text-[10px] tracking-[0.25em] uppercase text-sand-700 mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-sand-50 border border-sand-300 px-4 py-3 font-sans text-sand-900 focus:outline-none focus:border-brand-500 transition-colors"
      />
    </div>
  );
}
